import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAuth } from '../../contexts/AuthContext';
import { Target, Users, HeartHandshake, ArrowRight } from 'lucide-react';
import { checkIsMCAdmin } from '../../services/mcAPI';
import RoleToggle from '../leading/RoleToggle';

const LEADING_ADMIN_GROUP_ID = '0f0ec7be-6e06-4099-8bd5-c422980632fa';

const HomePage = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { getAccessToken } = useAuth();

  const [isLeadingAdmin, setIsLeadingAdmin] = useState(false);
  const [isMCAdmin, setIsMCAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      checkAccess();
    }
  }, [isAuthenticated]);

  const checkAccess = async () => {
    try {
      const accessToken = await getAccessToken();

      // Check both admin groups in parallel
      const [leadingRes, mcRes] = await Promise.allSettled([
        fetch(`${process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod'}/leading/api/users/me/is-admin`, {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
        }).then(r => r.json()),
        checkIsMCAdmin(accessToken)
      ]);

      if (leadingRes.status === 'fulfilled') {
        setIsLeadingAdmin(leadingRes.value?.data?.isAdmin || false);
      }
      if (mcRes.status === 'fulfilled') {
        setIsMCAdmin(mcRes.value?.isMCAdmin || false);
      }
    } catch (err) {
      console.error('Error checking access:', err);
    } finally {
      setLoading(false);
    }
  };

  const allApplications = [
    {
      id: 'operational-excellence',
      title: 'Operational Excellence',
      description: 'GPS Assessment System - Drive operational excellence through continuous improvement',
      icon: Target,
      path: '/gps',
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      requiredRole: 'leading',
      features: [
        'GPS Assessments & Benchmarking',
        'Team & Leadership Questionnaires',
        'Performance Analytics & Insights',
        'Continuous Improvement Tracking'
      ]
    },
    {
      id: 'leading-at-gallo',
      title: 'Leading at Gallo',
      description: 'Leadership Development & Excellence - Empowering leaders across the organization',
      icon: Users,
      path: '/leading',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      requiredRole: 'leading',
      features: [
        'Leadership Development Programs',
        'Competency Assessments',
        'Coaching & Mentoring Tools',
        'Leadership Analytics Dashboard'
      ]
    },
    {
      id: 'meaningful-conversations',
      title: 'Meaningful Conversations',
      description: 'Ensure leaders are having impactful 1:1 conversations with their team members',
      icon: HeartHandshake,
      path: '/conversations',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      requiredRole: 'mc',
      features: [
        '1:1 Conversation Surveys',
        'Leader-Team Member Feedback',
        'Conversation Quality Tracking',
        'Actionable Insights & Reports'
      ]
    }
  ];

  // Filter apps based on user's roles
  const applications = allApplications.filter(app => {
    if (app.requiredRole === 'leading') return isLeadingAdmin;
    if (app.requiredRole === 'mc') return isMCAdmin;
    return false;
  });

  const handleNavigation = (app) => {
    navigate(app.path);
  };

  if (loading) {
    return (
      <div className="home-page-container">
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', border: '4px solid rgba(0,0,0,0.1)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}></div>
            <p style={{ color: '#6b7280' }}>Checking access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="home-page-container">
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: '#1f1f1f', marginBottom: '1rem' }}>Access Denied</h2>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>You don't have admin access to any applications.</p>
            <a href="/" style={{ display: 'inline-block', padding: '0.75rem 1.5rem', background: '#6366f1', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>
              Back to My Surveys
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page-container">
      <RoleToggle />
      {/* Hero Section */}
      <div className="home-hero">
        <div className="container">
          <div className="home-hero-content">
            <h1 className="home-hero-title">Select Application</h1>
            <p className="home-hero-subtitle">Choose the admin area you want to access</p>
            <p className="home-hero-description">Manage operational excellence programs or leadership development initiatives</p>
          </div>
        </div>
      </div>

      {/* Application Cards */}
      <div className="home-content">
        <div className="container">
          <div className="home-apps-grid">
            {applications.map((app) => {
              const IconComponent = app.icon;
              return (
                <div key={app.id} className="home-app-card" style={{ position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: app.gradient }} />
                  <div className="home-app-icon" style={{ background: app.gradient, boxShadow: `0 8px 20px ${app.color}30` }}>
                    <IconComponent size={32} color="white" />
                  </div>
                  <h2 className="home-app-title">{app.title}</h2>
                  <p className="home-app-description">{app.description}</p>
                  <div className="home-app-features">
                    <ul>
                      {app.features.map((feature, index) => (
                        <li key={index}>
                          <div className="feature-dot" style={{ background: app.color }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => handleNavigation(app)}
                    className="home-app-button"
                    style={{ background: app.gradient, boxShadow: `0 4px 14px ${app.color}40` }}
                  >
                    <span>Access {app.title}</span>
                    <ArrowRight size={20} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Back to Surveys */}
      <div className="home-content" style={{ paddingTop: 0 }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <a
            href="/"
            className="home-app-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#6b7280', color: 'white', textDecoration: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: '600' }}
          >
            ← Back to My Surveys
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="home-footer">
        <div className="container">
          <p>Powered by Gallo Production System (GPS)</p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
